import { CarrierLogo } from "@/components/carriers/CarrierLogo";
import { DataTable } from "@/components/table/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { apiClient } from "@/lib/api/client";
import type { CarrierAccount, ConnectCarrierPayload } from "@/lib/api/types";
import {
  AVAILABLE_CARRIERS,
  getCarrierCredentialField,
  getCarrierCredentialRequirements,
  validateCarrierCredentialInput,
  type CarrierDefinition,
  type CarrierCredentialField,
  type CarrierCredentialFieldKey,
} from "@/lib/carriers";
import { Eye, EyeOff } from "lucide-react";
import * as React from "react";

type CarrierDialogMode = "create" | "edit";

type CarrierDialogForm = {
  id: string | null;
  carrierName: string;
  carrierCode: string;
  username: string;
  password: string;
  accountNumber: string;
  isActive: boolean;
};

type RequestCarrierForm = {
  carrierName: string;
  carrierWebsite: string;
  notes: string;
};

type CarrierSetupRequestSeed = {
  carrierName?: string;
  carrierCode?: string;
  notes?: string;
};

function sortCarriers(rows: CarrierAccount[]): CarrierAccount[] {
  return [...rows].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

function upsertCarrier(rows: CarrierAccount[], next: CarrierAccount): CarrierAccount[] {
  const index = rows.findIndex((row) => row.id === next.id);
  if (index === -1) {
    return sortCarriers([...rows, next]);
  }
  const copy = [...rows];
  copy[index] = next;
  return sortCarriers(copy);
}

const LTL_DEMO_CARRIER_CODES = new Set(["ROSI_DEMO", "SHIPPEEK_DEMO"]);
const PARCEL_DEMO_CARRIER_CODES = new Set(["SHIPPEEK_PARCEL_DEMO", "SHIPPEEK_DEMO_PARCEL"]);

function displayCarrierAccount(row: CarrierAccount): string {
  if (!row.accountNumber) return "-";
  return row.code.toLowerCase() === "usps" ? "Stored securely" : row.accountNumber;
}

function hasCarrierCode(rows: CarrierAccount[], codes: Set<string>): boolean {
  return rows.some((row) => codes.has(row.code.toUpperCase()));
}

function createEmptyDialogForm(): CarrierDialogForm {
  return {
    id: null,
    carrierName: "",
    carrierCode: "",
    username: "",
    password: "",
    accountNumber: "",
    isActive: true,
  };
}

function createEmptyRequestForm(): RequestCarrierForm {
  return { carrierName: "", carrierWebsite: "", notes: "" };
}

export default function CarriersPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState<CarrierDialogMode>("create");
  const [dialogSaving, setDialogSaving] = React.useState(false);
  const [demoSaving, setDemoSaving] = React.useState(false);
  const [dialogForm, setDialogForm] = React.useState<CarrierDialogForm>(createEmptyDialogForm());
  const [rows, setRows] = React.useState<CarrierAccount[]>([]);

  // Request carrier dialog
  const [requestDialogOpen, setRequestDialogOpen] = React.useState(false);
  const [requestForm, setRequestForm] = React.useState<RequestCarrierForm>(createEmptyRequestForm());
  const [requestSaving, setRequestSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getCarriers({ includeInactive: true });
      setRows(sortCarriers(data));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load carriers.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (!dialogOpen && !requestDialogOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (dialogOpen && !dialogSaving) setDialogOpen(false);
        if (requestDialogOpen && !requestSaving) setRequestDialogOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dialogOpen, dialogSaving, requestDialogOpen, requestSaving]);

  const setDialogTextField = React.useCallback(
    (field: "carrierName" | "carrierCode" | CarrierCredentialFieldKey, value: string) => {
      setDialogForm((current) => ({
        ...current,
        [field]: value,
      }));
    },
    [],
  );

  const openCreateDialog = React.useCallback(() => {
    setDialogMode("create");
    setDialogForm(createEmptyDialogForm());
    setDialogOpen(true);
  }, []);

  const openEditDialog = React.useCallback((carrier: CarrierAccount) => {
    setDialogMode("edit");
    setDialogForm({
      id: carrier.id,
      carrierName: carrier.name,
      carrierCode: carrier.code,
      username: carrier.username ?? "",
      password: carrier.password ?? "",
      accountNumber: carrier.accountNumber ?? "",
      isActive: carrier.isActive,
    });
    setDialogOpen(true);
  }, []);

  const closeDialog = React.useCallback(() => {
    if (dialogSaving) return;
    setDialogOpen(false);
  }, [dialogSaving]);

  const onDialogBackdropMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      closeDialog();
    },
    [closeDialog],
  );

  const onDialogBackdropKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      closeDialog();
    },
    [closeDialog],
  );

  const onSaveDialog = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);

    const carrierName = dialogForm.carrierName.trim();
    const carrierCode = dialogForm.carrierCode.trim();
    const username = dialogForm.username.trim();
    const password = dialogForm.password.trim();
    const accountNumber = dialogForm.accountNumber.trim();

    if (!carrierName || !carrierCode) {
      setError("Please select a carrier from the list.");
      return;
    }
    if (dialogMode === "create") {
      const credentialError = validateCarrierCredentialInput(carrierCode, { username, password, accountNumber });
      if (credentialError) {
        setError(credentialError);
        return;
      }
    }

    setDialogSaving(true);

    try {
      if (dialogMode === "create") {
        const payload: ConnectCarrierPayload = {
          carrierName,
          carrierCode,
          username,
          password,
          accountNumber: accountNumber || null,
        };
        const created = await apiClient.connectCarrier(payload);
        setRows((current) => upsertCarrier(current, created));
        setMessage(`${created.name} (${created.code || "N/A"}) connected.`);
      } else {
        const carrierId = dialogForm.id;
        if (!carrierId) {
          throw new Error("Carrier ID is missing.");
        }

        const updated = await apiClient.updateCarrier(carrierId, {
          carrierName,
          carrierCode,
          username: username || undefined,
          password: password || undefined,
          accountNumber: accountNumber || null,
          isActive: dialogForm.isActive,
        });
        setRows((current) => upsertCarrier(current, updated));
        setMessage(`${updated.name} updated.`);
      }

      setDialogOpen(false);
      setDialogForm(createEmptyDialogForm());
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save carrier.");
    } finally {
      setDialogSaving(false);
    }
  };

  const connectDemoCarrier = async () => {
    setError(null);
    setMessage(null);
    setDemoSaving(true);

    try {
      const created = await apiClient.connectDemoCarrier();
      setRows((current) => created.reduce((nextRows, carrier) => upsertCarrier(nextRows, carrier), current));
      setMessage("Demo carriers connected. Use a Dallas to Austin test checkout lane to see LTL and parcel demo rates.");
    } catch (demoError) {
      setError(demoError instanceof Error ? demoError.message : "Unable to connect the demo carrier.");
    } finally {
      setDemoSaving(false);
    }
  };

  // ── Request carrier handlers ────────────────────────────────────

  const openRequestDialog = React.useCallback((seed: CarrierSetupRequestSeed = {}) => {
    setDialogOpen(false);
    setRequestForm({
      carrierName: seed.carrierName ?? "",
      carrierWebsite: "",
      notes: seed.notes ?? "",
    });
    setRequestDialogOpen(true);
  }, []);

  const closeRequestDialog = React.useCallback(() => {
    if (requestSaving) return;
    setRequestDialogOpen(false);
  }, [requestSaving]);

  const onRequestBackdropMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      closeRequestDialog();
    },
    [closeRequestDialog],
  );

  const onRequestBackdropKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      closeRequestDialog();
    },
    [closeRequestDialog],
  );

  const onSubmitRequest = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);

    const carrierName = requestForm.carrierName.trim();
    if (!carrierName) {
      setError("Carrier name is required.");
      return;
    }

    setRequestSaving(true);
    try {
      await apiClient.requestCarrier({
        carrierName,
        carrierWebsite: requestForm.carrierWebsite.trim() || undefined,
        notes: requestForm.notes.trim() || undefined,
      });
      setMessage(`Carrier request for "${carrierName}" submitted. We'll get back to you within 24 hours.`);
      setRequestDialogOpen(false);
      setRequestForm(createEmptyRequestForm());
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to submit carrier request.");
    } finally {
      setRequestSaving(false);
    }
  };

  const hasLtlDemoCarrier = hasCarrierCode(rows, LTL_DEMO_CARRIER_CODES);
  const hasParcelDemoCarrier = hasCarrierCode(rows, PARCEL_DEMO_CARRIER_CODES);
  const missingDemoCarrier = !hasLtlDemoCarrier || !hasParcelDemoCarrier;

  return (
    <>
      <Card>
        <CardHeader className="gap-3 md:flex md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>Connected Carriers</CardTitle>
            <CardDescription>Manage connections and edit carrier settings from the table.</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={openCreateDialog} disabled={loading || dialogSaving}>
              Connect Carrier
            </Button>
            <Button type="button" variant="outline" onClick={() => void load()} disabled={loading || dialogSaving}>
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error ? <p className="mb-3 text-sm text-destructive">{error}</p> : null}
          {message ? <p className="mb-3 text-sm text-emerald-700">{message}</p> : null}
          {!loading && missingDemoCarrier ? (
            <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-medium">Need a carrier just to test checkout?</p>
                  <p className="mt-1 text-xs leading-5 text-emerald-800">
                    Add Shippeek demo carriers for LTL and parcel rates on the Dallas ↔ Austin test lane. No carrier account required.
                  </p>
                </div>
                <Button type="button" variant="outline" onClick={() => void connectDemoCarrier()} disabled={demoSaving || dialogSaving}>
                  {demoSaving ? "Connecting demos..." : "Add demo carriers"}
                </Button>
              </div>
            </div>
          ) : null}
          {loading ? (
            <div className="mb-3 space-y-3">
              <div className="flex gap-3">
                <Skeleton className="h-9 w-48" />
                <Skeleton className="h-9 w-32" />
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-28" />
                  <Skeleton className="h-8 w-24" />
                  <Skeleton className="h-8 w-16" />
                </div>
              ))}
            </div>
          ) : null}
          <DataTable
            rows={rows}
            getRowId={(row) => row.id}
            columns={[
              {
                key: "name",
                label: "Carrier",
                sortable: true,
                render: (row) => (
                  <div className="flex items-center gap-2">
                    <CarrierLogo code={row.code} name={row.name} className="size-8 rounded-md" />
                    <span className="font-medium">{row.name}</span>
                  </div>
                ),
                getSortValue: (row) => row.name,
                filter: { type: "text", placeholder: "Carrier" },
              },
              {
                key: "code",
                label: "Code",
                sortable: true,
                render: (row) => row.code || "-",
                getSortValue: (row) => row.code || "",
                filter: { type: "text", placeholder: "Code" },
              },
              {
                key: "account",
                label: "Account",
                render: displayCarrierAccount,
              },
              {
                key: "credentials",
                label: "Credentials",
                render: (row) => (row.hasCredentials ? "Connected" : "Missing"),
                filter: {
                  type: "select",
                  options: [
                    { label: "Connected", value: "Connected" },
                    { label: "Missing", value: "Missing" },
                  ],
                },
              },
              {
                key: "status",
                label: "Status",
                sortable: true,
                render: (row) =>
                  row.isActive ? (
                    <Badge variant="outline" className="font-normal border-emerald-300 bg-emerald-100 text-emerald-900">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="font-normal border-slate-300 bg-slate-100 text-slate-800">
                      Inactive
                    </Badge>
                  ),
                getSortValue: (row) => (row.isActive ? "Active" : "Inactive"),
                filter: {
                  type: "select",
                  options: [
                    {
                      label: "Active",
                      value: "Active",
                      badgeClassName: "border-emerald-300 bg-emerald-100 text-emerald-900",
                    },
                    {
                      label: "Inactive",
                      value: "Inactive",
                      badgeClassName: "border-slate-300 bg-slate-100 text-slate-800",
                    },
                  ],
                },
              },
              {
                key: "shipments",
                label: "Shipments",
                sortable: true,
                render: (row) => row.shipmentCount,
                getSortValue: (row) => row.shipmentCount,
              },
              {
                key: "actions",
                label: "Actions",
                render: (row) => (
                  <Button size="sm" variant="outline" onClick={() => openEditDialog(row)} disabled={loading || dialogSaving}>
                    Edit
                  </Button>
                ),
              },
            ]}
          />
        </CardContent>
      </Card>

      <CarrierDialog
        dialogOpen={dialogOpen}
        onDialogBackdropMouseDown={onDialogBackdropMouseDown}
        onDialogBackdropKeyDown={onDialogBackdropKeyDown}
        onSaveDialog={onSaveDialog}
        dialogForm={dialogForm}
        setDialogTextField={setDialogTextField}
        dialogMode={dialogMode}
        setDialogForm={setDialogForm}
        closeDialog={closeDialog}
        dialogSaving={dialogSaving}
        onRequestCarrier={openRequestDialog}
        connectedCodes={rows.map((r) => r.code)}
      />

      <RequestCarrierDialog
        open={requestDialogOpen}
        form={requestForm}
        setForm={setRequestForm}
        saving={requestSaving}
        onClose={closeRequestDialog}
        onBackdropMouseDown={onRequestBackdropMouseDown}
        onBackdropKeyDown={onRequestBackdropKeyDown}
        onSubmit={onSubmitRequest}
      />
    </>
  );
}

// ── Carrier Dialog ────────────────────────────────────────────────

type CarrierDialogProps = {
  dialogOpen: boolean;
  onDialogBackdropMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  onDialogBackdropKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onSaveDialog: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
  dialogForm: CarrierDialogForm;
  setDialogTextField: (
    field: "carrierName" | "carrierCode" | CarrierCredentialFieldKey,
    value: string,
  ) => void;
  dialogMode: CarrierDialogMode;
  setDialogForm: React.Dispatch<React.SetStateAction<CarrierDialogForm>>;
  closeDialog: () => void;
  dialogSaving: boolean;
  onRequestCarrier: (seed?: CarrierSetupRequestSeed) => void;
  connectedCodes: string[];
};

function CarrierDialog({
  dialogOpen,
  onDialogBackdropMouseDown,
  onDialogBackdropKeyDown,
  onSaveDialog,
  dialogForm,
  setDialogTextField,
  dialogMode,
  setDialogForm,
  closeDialog,
  dialogSaving,
  onRequestCarrier,
  connectedCodes,
}: CarrierDialogProps) {
  const [carrierSearch, setCarrierSearch] = React.useState("");
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Reset search when dialog opens/closes
  React.useEffect(() => {
    if (!dialogOpen) {
      setCarrierSearch("");
      setDropdownOpen(false);
      setShowPassword(false);
    }
  }, [dialogOpen]);

  // Close dropdown when clicking outside. Use capture because the modal
  // stops bubbling mouse events to keep backdrop clicks from closing it.
  React.useEffect(() => {
    if (!dropdownOpen) return undefined;
    const handler = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      const clickedDropdown = dropdownRef.current?.contains(target) ?? false;
      const clickedSearch = searchRef.current?.contains(target) ?? false;

      if (!clickedDropdown && !clickedSearch) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [dropdownOpen]);

  if (!dialogOpen) return null;
  const isEditMode = dialogMode === "edit";
  const credentialRequirements = getCarrierCredentialRequirements(dialogForm.carrierCode);
  const accountField = getCarrierCredentialField(dialogForm.carrierCode, "accountNumber");
  const dialogTitle = isEditMode ? "Edit Carrier" : "Connect Carrier";
  const dialogDescription = isEditMode
    ? "Update status and any credentials required by this carrier's API model."
    : "Select a carrier and enter the credentials required by that carrier's API model.";

  const connectedSet = new Set(connectedCodes.map((c) => c.toUpperCase()));

  const filteredCarriers = carrierSearch.trim()
    ? AVAILABLE_CARRIERS.filter((c) => {
        const q = carrierSearch.toLowerCase();
        return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
      })
    : AVAILABLE_CARRIERS;

  const selectCarrier = (carrier: CarrierDefinition) => {
    setDialogForm((current) => ({
      ...current,
      carrierName: carrier.name,
      carrierCode: carrier.code,
      username: "",
      password: "",
      accountNumber: "",
    }));
    setCarrierSearch("");
    setDropdownOpen(false);
  };

  const renderSetupBadge = (carrierCode: string) => {
    const requirements = getCarrierCredentialRequirements(carrierCode);
    return requirements.selfService ? (
      <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-[10px] font-normal text-emerald-800">
        Instant connect
      </Badge>
    ) : (
      <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] font-normal text-amber-800">
        2-day setup
      </Badge>
    );
  };

  const renderCredentialField = (field: CarrierCredentialField) => {
    const value = dialogForm[field.key];
    const isSecret = field.type === "password" || field.key === "password";
    const inputType = isSecret && !showPassword ? "password" : "text";
    const label = dialogMode === "create" || !field.required ? field.label : `${field.label} (optional)`;
    const placeholder = dialogMode === "edit" && field.key !== "accountNumber" ? "Leave blank to keep current" : field.placeholder;

    return (
      <div key={field.key} className="space-y-1.5">
        <Label htmlFor={`dialog-${field.key}`}>
          {label}
          {dialogMode === "create" && field.required ? <span className="text-destructive"> *</span> : null}
        </Label>
        <div className="relative">
          <Input
            id={`dialog-${field.key}`}
            type={inputType}
            value={value}
            onChange={(event) => setDialogTextField(field.key, event.target.value)}
            placeholder={placeholder}
            autoComplete={isSecret ? (dialogMode === "create" ? "new-password" : "current-password") : "off"}
            required={dialogMode === "create" && field.required}
            className={isSecret ? "pr-9" : undefined}
          />
          {isSecret ? (
            <button
              type="button"
              aria-label={showPassword ? `Hide ${field.label}` : `Show ${field.label}`}
              aria-pressed={showPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => setShowPassword((current) => !current)}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          ) : null}
        </div>
        {field.helpText ? <p className="text-xs text-muted-foreground">{field.helpText}</p> : null}
      </div>
    );
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Close carrier dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4"
      onMouseDown={onDialogBackdropMouseDown}
      onKeyDown={onDialogBackdropKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="carrier-dialog-title"
        className="w-full max-w-2xl rounded-lg border-[0.5px] border-border bg-card"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="border-b px-6 py-4">
          <h2 id="carrier-dialog-title" className="text-lg font-semibold">
            {dialogTitle}
          </h2>
          <p className="text-sm text-muted-foreground">{dialogDescription}</p>
        </div>

        <form className="space-y-6 px-6 py-5" onSubmit={onSaveDialog}>
          {/* Carrier selection */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Carrier</p>
            {isEditMode ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="dialogCarrierName">Name</Label>
                  <Input id="dialogCarrierName" value={dialogForm.carrierName} disabled required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dialogCarrierCode">Code</Label>
                  <Input id="dialogCarrierCode" value={dialogForm.carrierCode} disabled required />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {dialogForm.carrierCode ? (
                  <div className="flex items-center gap-3 rounded-md border border-border bg-muted/50 px-3 py-2">
                    <CarrierLogo code={dialogForm.carrierCode} name={dialogForm.carrierName} className="size-10" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium">{dialogForm.carrierName}</p>
                        {renderSetupBadge(dialogForm.carrierCode)}
                      </div>
                      <p className="text-xs text-muted-foreground">{dialogForm.carrierCode}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDialogForm((current) => ({ ...current, carrierName: "", carrierCode: "", username: "", password: "", accountNumber: "" }));
                        setDropdownOpen(true);
                        setTimeout(() => searchRef.current?.focus(), 0);
                      }}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      ref={searchRef}
                      placeholder="Search carriers..."
                      value={carrierSearch}
                      onChange={(e) => {
                        setCarrierSearch(e.target.value);
                        setDropdownOpen(true);
                      }}
                      onFocus={() => setDropdownOpen(true)}
                      autoComplete="off"
                    />
                    {dropdownOpen && (
                      <div
                        ref={dropdownRef}
                        className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
                      >
                        {filteredCarriers.length === 0 ? (
                          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                            No carriers match your search.
                          </div>
                        ) : (
                          filteredCarriers.map((carrier) => {
                            const alreadyConnected = connectedSet.has(carrier.code);
                            return (
                              <button
                                key={carrier.code}
                                type="button"
                                disabled={alreadyConnected}
                                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => selectCarrier(carrier)}
                              >
                                <CarrierLogo code={carrier.code} name={carrier.name} className="size-8 rounded-md" />
                                <div className="min-w-0 flex-1">
                                  <span className="font-medium">{carrier.name}</span>
                                  <span className="ml-2 text-xs text-muted-foreground">{carrier.code}</span>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  {renderSetupBadge(carrier.code)}
                                  <Badge variant="outline" className="text-[10px] font-normal">
                                    {carrier.type === "both" ? "Parcel + LTL" : carrier.type === "ltl" ? "LTL" : "Parcel"}
                                  </Badge>
                                </div>
                                {alreadyConnected && (
                                  <span className="text-[10px] text-muted-foreground">Connected</span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Don't see your carrier?{" "}
                  <button type="button" className="underline hover:text-foreground" onClick={() => onRequestCarrier()}>
                    Request a carrier
                  </button>{" "}
                  &mdash; turnaround time is 24 hours.
                </p>
              </div>
            )}
          </div>

          <div className="h-px bg-border" />

          {/* Credentials */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Credentials</p>
                <p className="mt-1 text-xs text-muted-foreground">{credentialRequirements.notes}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                {renderSetupBadge(dialogForm.carrierCode)}
                <Badge variant="outline" className="text-[10px] font-normal capitalize">
                  {credentialRequirements.authType.replaceAll("_", " ")}
                </Badge>
              </div>
            </div>

            {!credentialRequirements.selfService && dialogMode === "create" ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p>{credentialRequirements.notes}</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() =>
                    onRequestCarrier({
                      carrierName: dialogForm.carrierName,
                      carrierCode: dialogForm.carrierCode,
                      notes: `${dialogForm.carrierName} (${dialogForm.carrierCode}) requires Shippeek-assisted carrier setup.`,
                    })
                  }
                >
                  {credentialRequirements.supportCta ?? "Request setup"}
                </Button>
              </div>
            ) : credentialRequirements.fields.length > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {credentialRequirements.fields.map(renderCredentialField)}
                </div>
                {dialogForm.carrierCode ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      onRequestCarrier({
                        carrierName: dialogForm.carrierName,
                        carrierCode: dialogForm.carrierCode,
                        notes: `${dialogForm.carrierName} (${dialogForm.carrierCode}) customer needs help getting carrier API/developer account credentials. Auth model: ${credentialRequirements.authType}.`,
                      })
                    }
                  >
                    {credentialRequirements.supportCta ?? "Request setup help"}
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No self-serve credential fields are available for this carrier.</p>
            )}
          </div>

          <div className="h-px bg-border" />

          {/* Account settings */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Account Settings</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {!accountField ? (
                <div className="space-y-1.5">
                  <Label htmlFor="dialogAccountNumber">Account Number</Label>
                  <Input
                    id="dialogAccountNumber"
                    value={dialogForm.accountNumber}
                    onChange={(event) => setDialogTextField("accountNumber", event.target.value)}
                    placeholder="Optional"
                  />
                </div>
              ) : null}

              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={dialogForm.isActive ? "active" : "inactive"}
                  onValueChange={(value) =>
                    setDialogForm((current) => ({
                      ...current,
                      isActive: value === "active",
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={closeDialog} disabled={dialogSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={dialogSaving || (dialogMode === "create" && !credentialRequirements.selfService)}>
              {dialogSaving ? "Saving..." : dialogMode === "create" ? "Connect Carrier" : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Request Carrier Dialog ────────────────────────────────────────

type RequestCarrierDialogProps = {
  open: boolean;
  form: RequestCarrierForm;
  setForm: React.Dispatch<React.SetStateAction<RequestCarrierForm>>;
  saving: boolean;
  onClose: () => void;
  onBackdropMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  onBackdropKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
};

function RequestCarrierDialog({
  open,
  form,
  setForm,
  saving,
  onClose,
  onBackdropMouseDown,
  onBackdropKeyDown,
  onSubmit,
}: RequestCarrierDialogProps) {
  if (!open) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Close request carrier dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4"
      onMouseDown={onBackdropMouseDown}
      onKeyDown={onBackdropKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-carrier-dialog-title"
        className="w-full max-w-lg rounded-lg border-[0.5px] border-border bg-card"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="border-b px-6 py-4">
          <h2 id="request-carrier-dialog-title" className="text-lg font-semibold">
            Request Carrier Setup
          </h2>
          <p className="text-sm text-muted-foreground">
            Tell us which carrier needs API access or developer-account help. Our team will follow up.
          </p>
        </div>

        <form className="space-y-4 px-6 py-5" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="requestCarrierName">Carrier Name</Label>
            <Input
              id="requestCarrierName"
              value={form.carrierName}
              onChange={(e) => setForm((f) => ({ ...f, carrierName: e.target.value }))}
              placeholder="e.g. Acme Freight"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="requestCarrierWebsite">Carrier Website (optional)</Label>
            <Input
              id="requestCarrierWebsite"
              value={form.carrierWebsite}
              onChange={(e) => setForm((f) => ({ ...f, carrierWebsite: e.target.value }))}
              placeholder="https://acmefreight.com"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="requestCarrierNotes">Additional Notes (optional)</Label>
            <textarea
              id="requestCarrierNotes"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Developer portal status, account type, API approval needs, or any credentials you already have."
            />
          </div>

          <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
